import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { z } from "zod"
import { gql } from "~/__generated__"
import { AdminTagFragment } from "~/__generated__/graphql"
import { useDocumentTitle } from "~/common/use-document-title"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useValidationErrors } from "~/common/use-validation-errors"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/ui/alert-dialog"
import { Button } from "~/ui/button"
import { DelayedLoadingSpinner } from "~/ui/delayed-loading-spinner"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { Form, FormControl, FormField, FormItem } from "~/ui/form"
import { Input } from "~/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/ui/table"
import { useToast } from "~/ui/use-toast"

gql(/* GraphQL */ `
  fragment AdminTag on Tag {
    id
    name
    classification
    bookmarkEnrichmentTagsCount
    createdAt
  }
`)

const TAGS_QUERY = gql(/* GraphQL */ `
  query Tags($after: String, $filters: TagFiltersInput) {
    tags(first: 50, after: $after, filters: $filters) {
      edges {
        node {
          id
          ...AdminTag
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

const TAG_UPDATE_MUTATION = gql(/* GraphQL */ `
  mutation TagUpdate($input: TagUpdateInput!) {
    tagUpdate(input: $input) {
      tag {
        id
        name
      }
    }
  }
`)

const TAG_DELETE_MUTATION = gql(/* GraphQL */ `
  mutation TagDelete($input: TagDeleteInput!) {
    tagDelete(input: $input) {
      tag {
        id
      }
    }
  }
`)

const adminTagSearchSchema = z.object({
  search: z.string(),
})

const adminTagSearchSerializer = {
  serialize: (values: z.infer<typeof adminTagSearchSchema>) => {
    const params = new URLSearchParams()
    if (values.search) params.set("q", values.search)
    return params
  },
  lenientParse: (
    searchParams: URLSearchParams,
    defaultValues: z.infer<typeof adminTagSearchSchema>
  ) => {
    return {
      search: searchParams.get("q") ?? defaultValues.search,
    }
  },
}

interface TagFormValues {
  name: string
  classification: string
}

interface EditTagFormProps {
  tag: AdminTagFragment | null
  onSave: (values: TagFormValues) => Promise<void>
  onCancel: () => void
}

const EditTagForm: React.FC<EditTagFormProps> = ({ tag, onSave, onCancel }) => {
  const [updateTag, updateTagResult] = useSafeMutation(TAG_UPDATE_MUTATION)
  const editForm = useForm<TagFormValues>({
    defaultValues: {
      name: tag?.name || "",
      classification: tag?.classification || "GENERAL",
    },
  })

  useValidationErrors(editForm.setError, updateTagResult)

  if (!tag) return null

  const handleSubmit = async (values: TagFormValues) => {
    const { errors } = await updateTag({
      variables: {
        input: {
          id: tag.id,
          tagInput: {
            name: values.name,
            classification: values.classification as any,
          },
        },
      },
    })

    if (!errors) {
      onSave(values)
    }
  }

  return (
    <Form {...editForm}>
      <form onSubmit={editForm.handleSubmit(handleSubmit)} className="space-y-4">
        <FormItem>
          <FormControl>
            <Input
              {...editForm.register("name", { required: "Name is required" })}
              placeholder="Tag name"
            />
          </FormControl>
          {editForm.formState.errors.name && (
            <p className="text-sm text-red-500">{editForm.formState.errors.name.message}</p>
          )}
        </FormItem>
        <FormItem>
          <FormControl>
            <select
              {...editForm.register("classification")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            >
              <option value="GENERAL">General</option>
              <option value="CONTENT_TYPE">Content Type</option>
            </select>
          </FormControl>
        </FormItem>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Form>
  )
}

export const AdminTagsScreen: React.FC = () => {
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [editingTag, setEditingTag] = useState<AdminTagFragment | null>(null)
  const [deleteTagId, setDeleteTagId] = useState<string | null>(null)
  useDocumentTitle("Admin Tags")

  const form = useForm({
    resolver: zodResolver(adminTagSearchSchema),
    defaultValues: adminTagSearchSerializer.lenientParse(searchParams, { search: "" }),
  })

  const { watch } = form
  const searchValue = watch("search")

  const { data, loading, error, fetchMore, refetch } = useQuery(TAGS_QUERY, {
    variables: {
      after: null,
      filters: searchValue ? { search: searchValue } : null,
    },
  })

  const [deleteTag, deleteTagResult] = useSafeMutation(TAG_DELETE_MUTATION)

  // Monitor delete operation errors
  useEffect(() => {
    if (deleteTagResult.error) {
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      })
      setDeleteTagId(null)
    }
  }, [deleteTagResult.error, toast])

  useEffect(() => {
    const subscription = watch((values) => {
      // @ts-expect-error annoying type for 'values' from react-hook-form
      setSearchParams(adminTagSearchSerializer.serialize(values), { replace: true })
    })
    return () => subscription.unsubscribe()
  }, [watch, setSearchParams])

  useDocumentTitle("Admin Tags")

  if (error) {
    return <GraphqlError error={error} />
  }

  const tags = data?.tags.edges.map((edge) => edge.node) ?? []

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Tags</h1>
        <div className="mt-4">
          <Form {...form}>
            <form className="space-y-4">
              <FormField
                control={form.control}
                name="search"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="search"
                        placeholder="Search tags..."
                        {...field}
                        className="max-w-sm"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </form>
          </Form>
          {loading && <DelayedLoadingSpinner className="mt-2" />}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Classification</TableHead>
            <TableHead>Bookmarks</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tags.map((tag) => (
            <TableRow key={tag.id}>
              <TableCell>{tag.name}</TableCell>
              <TableCell className="font-mono">{tag.classification}</TableCell>
              <TableCell>{tag.bookmarkEnrichmentTagsCount}</TableCell>
              <TableCell>{new Date(tag.createdAt).toLocaleString()}</TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingTag({ ...tag })}
                  className="mr-2"
                >
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteTagId(tag.id)}>
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {tags.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                No tags found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data?.tags.pageInfo.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => {
              fetchMore({
                variables: {
                  after: data.tags.pageInfo.endCursor,
                  filters: searchValue ? { search: searchValue } : null,
                },
              })
            }}
          >
            Load more
          </Button>
        </div>
      )}

      {/* Edit Tag Dialog */}
      <Dialog open={!!editingTag} onOpenChange={(open) => !open && setEditingTag(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
          </DialogHeader>
          <EditTagForm
            tag={editingTag}
            onSave={async () => {
              toast({
                title: "Success",
                description: "Tag updated successfully",
              })
              setEditingTag(null)
              refetch()
            }}
            onCancel={() => setEditingTag(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Tag Dialog */}
      <AlertDialog open={!!deleteTagId} onOpenChange={() => setDeleteTagId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this tag? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteTagId) return

                const { errors } = await deleteTag({
                  variables: {
                    input: {
                      id: deleteTagId,
                    },
                  },
                })

                if (!errors) {
                  toast({
                    title: "Success",
                    description: "Tag deleted successfully",
                  })
                  setDeleteTagId(null)
                  refetch()
                } else {
                  toast({
                    title: "Error",
                    description: "Failed to delete tag",
                    variant: "destructive",
                  })
                  setDeleteTagId(null)
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
